// src/hooks/useCacheWarmer.js
// App-wide cache warming on foreground.
// Fires when the user returns to the app after it was backgrounded.
// Refreshes all critical data sources simultaneously so cached data
// is always as fresh as the last time the user had the app open.
//
// Critical sources:
//   nws_alerts     — weather warnings change every few hours
//   usgs_gauges    — gauge readings change every 15 min
//   511la_incidents— road conditions change constantly during events
//
// Does NOT fire on first mount (app startup has its own loading path).
// Only fires on foreground transitions (tab switch / phone unlock).

import { useEffect, useRef } from 'react';
import { StaleCacheManager } from '@services/sync/staleCache';
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const jitter = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;


const CRITICAL_KEYS = ['nws_alerts', 'usgs_gauges', '511la_incidents'];

// Minimum gap between foreground revalidations (30 seconds)
// Prevents rapid revalidation if user switches tabs quickly
const MIN_REVALIDATE_GAP_MS  = 30_000;
// Reconnect jitter: when devices regain signal after an outage,
// thousands of phones may reconnect simultaneously. A random 5-90s
// delay staggers the sync flood across the server's time window.
const RECONNECT_JITTER_MIN_MS = 5_000;   //  5 seconds
const RECONNECT_JITTER_MAX_MS = 90_000;  // 90 seconds

export function useCacheWarmer() {
  const lastWarmedAt    = useRef(0);
  const isFirstMount    = useRef(true);

  useEffect(() => {
    // Skip the first mount — app startup already triggers data loads
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!navigator.onLine) return;  // pointless offline

      const now = Date.now();
      if (now - lastWarmedAt.current < MIN_REVALIDATE_GAP_MS) return;
      lastWarmedAt.current = now;

      // Stagger reconnect: add random jitter so thousands of phones
      // don't all hit Firebase simultaneously after an outage.
      const wasOffline = (now - lastWarmedAt.current) > 5 * 60_000; // was offline > 5 min
      if (wasOffline) {
        const delay = jitter(RECONNECT_JITTER_MIN_MS, RECONNECT_JITTER_MAX_MS);
        await sleep(delay);
      }

      // Revalidate all critical cache keys simultaneously
      CRITICAL_KEYS.forEach((key) => {
        StaleCacheManager.revalidate(key).catch(() => {});
      });

      // Also nudge the SW to run background sync
      navigator.serviceWorker?.ready?.then((reg) => {
        reg.sync?.register('na-sync').catch(() => {});
      });
    };

    document.addEventListener('visibilitychange', handleVisibility);

    // Also warm on window focus (desktop browser)
    window.addEventListener('focus', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, []);
}

// ── Critical chunk pre-warmer ─────────────────────────────────────
// Preloads ALL lazy-loaded route chunks so they're available offline.
// Runs once after first mount, staggered to avoid blocking the UI.
export function useChunkPrewarmer() {
  useEffect(() => {
    if (!navigator.onLine) return;
    const CRITICAL_CHUNKS = [
      // Core navigation targets
      () => import('@features/travel/SafeToTravelPage'),
      () => import('@features/maps/DownloadMapPage'),
      () => import('@features/neighborhood/NeighborhoodPage'),
      () => import('@features/maps/MapPage'),
      () => import('@features/alerts/AlertsPage'),
      () => import('@features/more/MorePage'),
      () => import('@features/radio/RadioPage'),
      // Tools tab targets
      () => import('@features/contacts/ContactsPage'),
      () => import('@features/contacts/HospitalsPage'),
      () => import('@features/guides/EmergencyPlanPage'),
      () => import('@features/guides/FirstAidPage'),
      () => import('@features/binder/EmergencyBinder'),
      () => import('@features/tools/EmergencyToolsPage'),
      () => import('@features/more/AccessibilityPage'),
      // Other navigated routes
      () => import('@features/evacuation/EvacPage'),
      () => import('@features/resources/ResourcesPage'),
      () => import('@features/connectivity/HotspotsPage'),
      () => import('@features/more/PumpStationMap'),
      () => import('@features/readiness/ReadinessPage'),
      () => import('@features/legal/AccuracyPage'),
      () => import('@features/legal/LegalPage'),
      () => import('@features/alerts/NewsPage'),
    ];

    let i = 0;
    const preloadNext = () => {
      if (i >= CRITICAL_CHUNKS.length || !navigator.onLine) return;
      CRITICAL_CHUNKS[i++]().catch(() => {}).finally(() => {
        setTimeout(preloadNext, 300);
      });
    };

    // Start after 4s to let critical data load first
    const t = setTimeout(preloadNext, 4000);
    return () => clearTimeout(t);
  }, []);
}
